import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Bookmark } from "lucide-react";

import { EmptyState } from "@/components/empty-state";
import { StoryCard, StoryCardSkeleton } from "@/components/story-card";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth-context";
import { fetchBookmarkedStories } from "@/lib/queries";

export const Route = createFileRoute("/_authenticated/library")({
  head: () => ({
    meta: [
      { title: "Your library — Quibblies" },
      {
        name: "description",
        content: "Bookmarked stories and reading progress saved to your Quibblies library.",
      },
      { property: "og:title", content: "Your library — Quibblies" },
      { property: "og:description", content: "Your saved stories on Quibblies." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
  }),
  component: LibraryPage,
});

function LibraryPage() {
  const { user } = useAuth();
  const bookmarks = useQuery({
    queryKey: ["library", user?.id],
    queryFn: () => fetchBookmarkedStories(user!.id),
    enabled: Boolean(user),
  });

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="font-display text-4xl text-foreground">Your library</h1>
      <p className="mt-2 text-sm text-muted-foreground">Everything you've bookmarked.</p>

      <div className="mt-8">
        {bookmarks.isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {Array.from({ length: 3 }).map((_, index) => (
              <StoryCardSkeleton key={index} />
            ))}
          </div>
        ) : (bookmarks.data ?? []).length === 0 ? (
          <EmptyState
            icon={Bookmark}
            title="Your library is empty"
            description="Bookmark a story while reading and it will wait for you here."
            action={
              <Button asChild variant="soft">
                <Link to="/discover">Find something to read</Link>
              </Button>
            }
          />
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {bookmarks.data!.map((story) => (
              <StoryCard key={story.id} story={story} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
